import React, { useEffect, useState } from 'react';
import './style.scss';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { DefaultSwitch } from 'services/UIUtils';
import ProgressMessage from 'components/Utils/ProgressMessage';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import type { StaffRole } from 'generated/api';

/**
 * Tenant-defined staff roles — what this clinic calls its people, and the
 * rules that follow from each ("can hold a patient panel", "counts in
 * clinician statistics", what their registration number is called).
 *
 * Distinct from the account type (patient/physician/admin) that controls
 * access: a receptionist and an office manager both sign in as admin staff
 * and are different staff roles here.
 *
 * Roles are referenced by id everywhere, so renaming one is safe — existing
 * staff assignments and booking rules follow the rename automatically.
 */

// Presets for the name field. Free text is always allowed; these just save
// typing for the roles most clinics have.
const COMMON_ROLES = [
  'Nurse Practitioner',
  'Registered Nurse',
  'RPN',
  'Family Physician',
  'Physician',
  'Resident',
  'Phlebotomist',
  'Medical Office Assistant',
  'Receptionist',
  'Office Manager',
  'Practice Manager',
  'Dietitian',
  'Social Worker',
  'Pharmacist',
  'Physiotherapist',
];

const APPLIES_TO_OPTIONS = [
  { value: 'physician', label: 'Practitioners' },
  { value: 'admin', label: 'Admin staff' },
];

const LABEL_MAX = 60;
const SHORT_LABEL_MAX = 20;
const COLLEGE_LABEL_MAX = 30;

const blankRole = (): StaffRole => ({ label: '' }) as StaffRole;

const StaffRoles = () => {
  const dispatch = useAppDispatch();
  const [roles, setRoles] = useState<StaffRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [savingId, setSavingId] = useState<string | undefined>();
  const [draft, setDraft] = useState<StaffRole | undefined>();
  const [editingId, setEditingId] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  async function load() {
    setLoading(true);
    // Retired roles are included so they can be reactivated — they're hidden
    // from pickers elsewhere, but this screen is where you'd bring one back.
    const result = await dispatch(api.Staff.getStaffRoles({ includeInactive: true }), {
      errorTitle: 'Staff Roles',
    });
    // Only trust a real payload: a failed request resolves undefined, and
    // rendering that as an empty registry would invite an admin to recreate
    // roles that already exist.
    if (result) {
      setRoles(result);
      setLoadFailed(false);
    } else {
      setLoadFailed(true);
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function patch(id: string | undefined, changes: Partial<StaffRole>) {
    setError(undefined);
    if (id) {
      setEditingId(id);
      setRoles((prev) => prev.map((it) => (it.id === id ? { ...it, ...changes } : it)));
    } else {
      setDraft((prev) => ({ ...(prev ?? blankRole()), ...changes }));
    }
  }

  /** Client-side mirror of the server's rules, so a rejected save doesn't cost
   * the admin their typing. The server validates regardless. */
  function validationError(role: StaffRole): string | undefined {
    if (!role.label?.trim()) return 'A role needs a name.';
    if (role.label.trim().length > LABEL_MAX)
      return `Role name is too long (max ${LABEL_MAX} characters).`;
    if ((role.short_label?.trim().length ?? 0) > SHORT_LABEL_MAX)
      return `Short form is too long (max ${SHORT_LABEL_MAX} characters).`;
    if ((role.college_label?.trim().length ?? 0) > COLLEGE_LABEL_MAX)
      return `Registration label is too long (max ${COLLEGE_LABEL_MAX} characters).`;
    return undefined;
  }

  async function saveRole(role: StaffRole) {
    const invalid = validationError(role);
    setError(invalid);
    if (invalid) return;

    setSavingId(role.id ?? 'new');
    const result = role.id
      ? await dispatch(api.Staff.updateStaffRole({ id: role.id, staffRole: role }), {
          title: 'Staff Roles',
          successText: 'Role saved',
        })
      : await dispatch(api.Staff.createStaffRole({ staffRole: role }), {
          title: 'Staff Roles',
          successText: 'Role added',
        });
    setSavingId(undefined);

    // A failed save resolves undefined — keep the card and the admin's input on
    // screen so they can correct and retry, rather than silently discarding it.
    if (!result) return;

    setDraft(undefined);
    setEditingId(undefined);
    await load();
  }

  const roleCard = (role: StaffRole, isDraft = false) => {
    const id = isDraft ? undefined : role.id;
    const busy = savingId === (role.id ?? 'new');
    // Editing several cards then saving one would lose the others on reload,
    // so only the card being edited is interactive.
    const blocked = !isDraft && !!editingId && editingId !== role.id;
    const appliesToAll = !role.applies_to?.length;
    // Absent applies_to means "any", so an unscoped role counts as one a
    // practitioner could hold.
    const appliesToPractitioners = appliesToAll || role.applies_to!.includes('physician' as any);

    return (
      <div
        className={`staff-role-card ${role.inactive ? 'retired' : ''} ${blocked ? 'blocked' : ''}`}
        key={role.id ?? 'new'}
      >
        <div className="staff-role-row">
          <DefaultTextField
            label="Role name"
            name={`role-label-${role.id ?? 'new'}`}
            placeholder="e.g. Nurse Practitioner"
            value={role.label ?? ''}
            disabled={blocked}
            htmlInputProps={{ list: 'staff-role-presets', maxLength: LABEL_MAX }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              patch(id, { label: e.target.value })
            }
            style={{ width: '260px' }}
          />
          <DefaultTextField
            label="Short form"
            name={`role-short-${role.id ?? 'new'}`}
            placeholder="e.g. NP"
            value={role.short_label ?? ''}
            disabled={blocked}
            htmlInputProps={{ maxLength: SHORT_LABEL_MAX }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              patch(id, { short_label: e.target.value || undefined })
            }
            style={{ width: '140px' }}
          />
          <DefaultTextField
            label="Registration number is called"
            name={`role-college-${role.id ?? 'new'}`}
            placeholder="e.g. CNO, CPSO"
            value={role.college_label ?? ''}
            disabled={blocked}
            htmlInputProps={{ maxLength: COLLEGE_LABEL_MAX }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              patch(id, { college_label: e.target.value || undefined })
            }
            style={{ width: '220px' }}
          />
          <DefaultTextField
            label="Order"
            name={`role-order-${role.id ?? 'new'}`}
            type="number"
            value={role.display_order ?? ''}
            disabled={blocked}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const n = Number(e.target.value);
              if (e.target.value !== '' && (!Number.isInteger(n) || n < 0)) return;
              patch(id, { display_order: e.target.value === '' ? undefined : n });
            }}
            style={{ width: '90px' }}
          />
        </div>

        <div className="staff-role-row staff-role-switches">
          <DefaultSwitch
            label="Can be a primary practitioner"
            name={`role-primary-${role.id ?? 'new'}`}
            disabled={blocked}
            value={!!role.can_be_primary}
            checked={!!role.can_be_primary}
            onChange={(_: any, checked: boolean) => patch(id, { can_be_primary: checked })}
          />
          <DefaultSwitch
            label="Counts in clinician statistics"
            name={`role-stats-${role.id ?? 'new'}`}
            disabled={blocked}
            value={role.counts_as_clinician ?? !!role.can_be_primary}
            checked={role.counts_as_clinician ?? !!role.can_be_primary}
            onChange={(_: any, checked: boolean) => patch(id, { counts_as_clinician: checked })}
          />
          {/* Both of these are practitioner concepts — the medical-order gate
              only ever tests a practitioner record, and nursing work is routed
              to one. Hiding them for admin-only roles keeps the editor from
              asking a question that has no meaning for a Receptionist. */}
          {appliesToPractitioners && (
            <>
              <DefaultSwitch
                label="Can place medical orders"
                name={`role-orders-${role.id ?? 'new'}`}
                disabled={blocked}
                // Shown as ON when unset, which is what an unset flag means for
                // a NEW role: unrestricted. Displaying it as OFF would read as
                // "forbidden" — the same mistake the old practitioner-type
                // dropdown made by rendering its default as if it were a choice.
                value={role.can_place_medical_orders ?? true}
                checked={role.can_place_medical_orders ?? true}
                onChange={(_: any, checked: boolean) =>
                  patch(id, { can_place_medical_orders: checked })
                }
              />
              <DefaultSwitch
                label="Receives nursing tasks"
                name={`role-nursing-${role.id ?? 'new'}`}
                disabled={blocked}
                value={!!role.handles_nursing_tasks}
                checked={!!role.handles_nursing_tasks}
                onChange={(_: any, checked: boolean) =>
                  patch(id, { handles_nursing_tasks: checked })
                }
              />
            </>
          )}
          {APPLIES_TO_OPTIONS.map(({ value, label }) => (
            <DefaultSwitch
              key={value}
              label={label}
              name={`role-applies-${value}-${role.id ?? 'new'}`}
              disabled={blocked}
              value={appliesToAll || role.applies_to!.includes(value as any)}
              checked={appliesToAll || role.applies_to!.includes(value as any)}
              onChange={(_: any, checked: boolean) => {
                const current = appliesToAll
                  ? APPLIES_TO_OPTIONS.map((it) => it.value)
                  : [...role.applies_to!];
                const next = checked
                  ? Array.from(new Set([...current, value]))
                  : current.filter((it) => it !== value);
                // A role that applies to nobody would vanish from every picker,
                // and the server reads an empty list as "anyone" — which would
                // silently re-tick both. Refuse the last untick instead.
                if (!next.length) {
                  setError('A role has to apply to practitioners, admin staff, or both.');
                  return;
                }
                patch(id, { applies_to: next as any });
              }}
            />
          ))}
        </div>

        <div className="staff-role-row staff-role-actions">
          {!isDraft && (
            <DefaultSwitch
              label="Retired"
              name={`role-inactive-${role.id}`}
              disabled={blocked}
              value={!!role.inactive}
              checked={!!role.inactive}
              onChange={(_: any, checked: boolean) => patch(id, { inactive: checked })}
            />
          )}
          <div className="flex-grow" />
          {isDraft && (
            <DefaultButton
              name="Cancel"
              className="light"
              onClick={() => {
                setDraft(undefined);
                setError(undefined);
              }}
              style={{ width: '120px' }}
            />
          )}
          {!isDraft && editingId === role.id && (
            <DefaultButton
              name="Discard"
              className="light"
              onClick={() => {
                setEditingId(undefined);
                setError(undefined);
                load();
              }}
              style={{ width: '120px' }}
            />
          )}
          <DefaultButton
            name={isDraft ? 'Add role' : 'Save'}
            className="blue"
            loading={busy}
            disabled={blocked}
            onClick={() => saveRole(role)}
            style={{ width: '120px' }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="full-height-panel full-width section-cards-layout staff-roles">
      <div className="section">
        <span className="small-title">Staff Roles</span>
        <p className="hint">
          What your clinic calls its people, and the rules that follow from each. This is separate
          from the account type that controls what someone can access — a receptionist and an office
          manager both sign in as admin staff, but are different roles here.
        </p>
        <p className="hint">
          Renaming a role is safe — everyone assigned to it is linked by identity, not by name, so
          the rename follows them. Retire a role instead of deleting it so existing assignments keep
          working.
        </p>
        <p className="hint">
          Scheduling, statements and statistics still read a practitioner&apos;s legacy type today;
          they move over to these roles in a later release.
        </p>

        {/* Native datalist: presets for convenience, free text always allowed. */}
        <datalist id="staff-role-presets">
          {COMMON_ROLES.map((it) => (
            <option key={it} value={it} />
          ))}
        </datalist>

        {error && <p className="staff-role-error">{error}</p>}

        <ProgressMessage size={30} show={loading} className="my-5" />

        {!loading && loadFailed && (
          <div className="staff-role-load-failed">
            <p className="staff-role-error">Couldn&apos;t load staff roles.</p>
            <DefaultButton
              name="Retry"
              className="light"
              onClick={load}
              style={{ width: '120px' }}
            />
          </div>
        )}

        {!loading && !loadFailed && (
          <>
            {roles.length === 0 && !draft && (
              <p className="hint">No staff roles yet — add the first one below.</p>
            )}
            {roles.map((role) => roleCard(role))}
            {draft && roleCard(draft, true)}
            {!draft && (
              <div className="row button-wrapper">
                <DefaultButton
                  name="Add a role"
                  className="light"
                  icon="plus"
                  onClick={() => {
                    setError(undefined);
                    setDraft(blankRole());
                  }}
                  style={{ width: '160px' }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StaffRoles;
